/**
 * Admin notification delivery status (ACC-12). For the tenant's most recent
 * notifications, shows each recipient and that recipient's per-channel send
 * attempts (EMAIL/TEAMS → SENDING/SENT/FAILED) from the notification_deliveries
 * ledger the dispatcher writes. Recipient renders the resolved display name,
 * falling back to email then "Unknown recipient" — never the raw contact/user
 * id, which stays reachable via the cell's title. The "In-app" column shows
 * the bell read state in human terms (Unread/Read, or Failed for the rare
 * failure case) — the human-readable meaning of
 * notification_recipients.delivery_status; the raw persisted value rides
 * data-in-app-status so it stays cross-checkable. In-app read state and
 * channel send outcomes are deliberately independent signals. Read-only;
 * admin/internal only (RequireAdminRoute plus the ryugu RPC's own role gate).
 *
 * The `event` query param (?event=<id>) drills into one notification event,
 * so a link can point at exactly the row it means instead of a tenant-wide,
 * capped, newest-first window that other notification volume can push a
 * given event off of (SAD-322). Omitting it keeps today's unfiltered default.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Send } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useNotificationDeliveryStatus } from './hooks';
/** SENT reads as success, FAILED as an alarm, SENDING/unknown as in-flight. */ function channelVariant(status) {
    if (status === 'SENT') return 'default';
    if (status === 'FAILED') return 'destructive';
    return 'outline';
}
function ChannelBadges({ channels }) {
    if (channels.length === 0) {
        return /*#__PURE__*/ _jsx("span", {
            className: "text-muted-foreground",
            children: "—"
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "flex flex-wrap gap-1",
        children: channels.map((c)=>/*#__PURE__*/ _jsxs(Badge, {
                variant: channelVariant(c.status),
                "data-testid": Testids.adminNotificationDelivery.channel,
                "data-channel": c.channel,
                "data-status": c.status,
                title: c.error || undefined,
                children: [
                    c.channel,
                    ": ",
                    c.status
                ]
            }, `${c.channel}-${c.status}`))
    });
}
// The persisted value is the bell's read state, not a transport outcome — see
// the file doc comment. Map it to the verb the bell itself uses ("N unread",
// "Mark read") rather than inventing new vocabulary. An unmapped value still
// renders as-is so an unknown state is never hidden.
const IN_APP_LABELS = {
    PENDING: 'Unread',
    DELIVERED: 'Read',
    FAILED: 'Failed'
};
function inAppLabel(status) {
    return IN_APP_LABELS[status] ?? status;
}
/** Unread should read as the one needing attention; Read recedes. */ function inAppVariant(status) {
    if (status === 'PENDING') return 'default';
    if (status === 'DELIVERED') return 'secondary';
    return 'outline';
}
function RecipientCell({ recipient: r, duplicateName }) {
    const rawId = r.contactId || r.platformUserId || r.recipientId;
    const testId = Testids.adminNotificationDelivery.recipient;
    if (r.recipientDisplayName) {
        return /*#__PURE__*/ _jsxs(TableCell, {
            "data-testid": testId,
            title: rawId || undefined,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    children: r.recipientDisplayName
                }),
                duplicateName && r.recipientEmail && /*#__PURE__*/ _jsx("div", {
                    className: "text-xs text-muted-foreground",
                    children: r.recipientEmail
                })
            ]
        });
    }
    if (r.recipientEmail) {
        return /*#__PURE__*/ _jsx(TableCell, {
            "data-testid": testId,
            title: rawId || undefined,
            children: r.recipientEmail
        });
    }
    return /*#__PURE__*/ _jsx(TableCell, {
        "data-testid": testId,
        title: rawId || undefined,
        className: "text-muted-foreground",
        children: "Unknown recipient"
    });
}
export function NotificationDeliveryPage() {
    const [searchParams] = useSearchParams();
    const eventID = searchParams.get('event') ?? '';
    const { data: statuses, isLoading, error } = useNotificationDeliveryStatus(eventID);
    // One flat row per (notification, recipient). Computed up front so the empty
    // state is driven by the actual row count — a notification that resolved to
    // no recipients would otherwise render an empty table with no message.
    const rows = (statuses ?? []).flatMap((s)=>s.recipients.map((r)=>({
                status: s,
                recipient: r
            })));
    // Display names aren't unique — count occurrences across the visible rows
    // so the recipient cell only shows the disambiguating email when two rows
    // would otherwise render the same label.
    const nameCounts = new Map();
    for (const { recipient: r } of rows){
        if (r.recipientDisplayName) {
            nameCounts.set(r.recipientDisplayName, (nameCounts.get(r.recipientDisplayName) ?? 0) + 1);
        }
    }
    return /*#__PURE__*/ _jsxs(Card, {
        "data-testid": Testids.adminNotificationDelivery.page,
        children: [
            /*#__PURE__*/ _jsxs(CardHeader, {
                children: [
                    /*#__PURE__*/ _jsxs(CardTitle, {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(Send, {
                                className: "h-4 w-4"
                            }),
                            " Notification delivery status"
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CardDescription, {
                        children: "Recent notifications with each recipient's per-channel delivery state. In-app shows whether the recipient has read it in the bell; Channels shows whether email/Teams actually sent — the two move independently."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(CardContent, {
                children: [
                    isLoading && /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground",
                        children: "Loading…"
                    }),
                    error && /*#__PURE__*/ _jsxs("p", {
                        className: "text-destructive",
                        children: [
                            "Failed to load delivery status: ",
                            String(error)
                        ]
                    }),
                    statuses && rows.length === 0 && /*#__PURE__*/ _jsx("p", {
                        "data-testid": Testids.adminNotificationDelivery.empty,
                        className: "text-muted-foreground",
                        children: "No notifications with delivery records yet."
                    }),
                    rows.length > 0 && /*#__PURE__*/ _jsxs(Table, {
                        "data-testid": Testids.adminNotificationDelivery.table,
                        children: [
                            /*#__PURE__*/ _jsx(TableHeader, {
                                children: /*#__PURE__*/ _jsxs(TableRow, {
                                    children: [
                                        /*#__PURE__*/ _jsx(TableHead, {
                                            children: "Notification"
                                        }),
                                        /*#__PURE__*/ _jsx(TableHead, {
                                            children: "Recipient"
                                        }),
                                        /*#__PURE__*/ _jsx(TableHead, {
                                            children: "In-app"
                                        }),
                                        /*#__PURE__*/ _jsx(TableHead, {
                                            children: "Channels"
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(TableBody, {
                                children: rows.map(({ status: s, recipient: r })=>/*#__PURE__*/ _jsxs(TableRow, {
                                        "data-testid": Testids.adminNotificationDelivery.row,
                                        "data-notification-id": s.notificationEventId,
                                        "data-notification-type": s.notificationType,
                                        "data-recipient-id": r.recipientId,
                                        children: [
                                            /*#__PURE__*/ _jsxs(TableCell, {
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "font-medium",
                                                        children: s.title
                                                    }),
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "text-xs text-muted-foreground",
                                                        children: s.notificationType
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(RecipientCell, {
                                                recipient: r,
                                                duplicateName: (nameCounts.get(r.recipientDisplayName) ?? 0) > 1
                                            }),
                                            /*#__PURE__*/ _jsx(TableCell, {
                                                children: /*#__PURE__*/ _jsx(Badge, {
                                                    variant: inAppVariant(r.inAppStatus),
                                                    "data-testid": Testids.adminNotificationDelivery.inApp,
                                                    "data-in-app-status": r.inAppStatus,
                                                    title: r.acknowledgedAt ? r.acknowledgedAt.toDate().toLocaleString() : undefined,
                                                    children: inAppLabel(r.inAppStatus)
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(TableCell, {
                                                children: /*#__PURE__*/ _jsx(ChannelBadges, {
                                                    channels: r.channelDeliveries
                                                })
                                            })
                                        ]
                                    }, `${s.notificationEventId}-${r.recipientId}`))
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
